import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getCouncils, startScrape, getScrapeStatus, getScrapeJobs, getAdminStats,
  getGoogleStatus, startGoogleBackfill, getTowns, purgeLegacy,
  getApifyStatus, startEmailEnrich, startSocialEnrich,
} from '../services/api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Building2, DownloadCloud, Store, MapPin, Database, Loader2, CheckCircle2, XCircle, Clock, Phone, Trash2,
  Mail, Share2,
} from 'lucide-react';

const BUSINESS_TYPES = [
  { value: 'restaurant', label: 'Restaurants' },
  { value: 'cafe', label: 'Cafés' },
  { value: 'takeaway', label: 'Takeaways' },
  { value: 'pub', label: 'Pubs & Bars' },
];

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-white border border-[#E8E8E8] rounded-xl p-5 flex items-center gap-4" data-testid={`stat-${label.toLowerCase()}`}>
    <div className="w-11 h-11 rounded-lg bg-[#F3E8FF] flex items-center justify-center">
      <Icon className="h-5 w-5 text-[#7B2CBF]" />
    </div>
    <div>
      <p className="text-2xl font-bold text-[#242E30] leading-none">{value ?? '-'}</p>
      <p className="text-xs text-[#595959] mt-1">{label}</p>
    </div>
  </div>
);

const AdminPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [councils, setCouncils] = useState([]);
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);

  const [authority, setAuthority] = useState('');
  const [town, setTown] = useState('');
  const [types, setTypes] = useState(['restaurant']);
  const [attachHygiene, setAttachHygiene] = useState(true);
  const [includeGoogle, setIncludeGoogle] = useState(false);

  const [googleStatus, setGoogleStatus] = useState(null);
  const [towns, setTowns] = useState([]);
  const [bfTown, setBfTown] = useState('all');
  const [bfLimit, setBfLimit] = useState(20);
  const [bfJob, setBfJob] = useState(null);
  const [bfSubmitting, setBfSubmitting] = useState(false);
  const bfPollRef = useRef(null);
  const [purging, setPurging] = useState(false);

  // Apify contact enrichment
  const [apifyStatus, setApifyStatus] = useState(null);
  const [emailTown, setEmailTown] = useState('all');
  const [emailLimit, setEmailLimit] = useState(50);
  const [emailJob, setEmailJob] = useState(null);
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const emailPollRef = useRef(null);
  const [socialTown, setSocialTown] = useState('all');
  const [socialLimit, setSocialLimit] = useState(25);
  const [socialJob, setSocialJob] = useState(null);
  const [socialSubmitting, setSocialSubmitting] = useState(false);
  const socialPollRef = useRef(null);

  const [activeJob, setActiveJob] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const pollRef = useRef(null);

  const loadMeta = useCallback(async () => {
    try {
      const [s, j, gs, t, as] = await Promise.all([
        getAdminStats(), getScrapeJobs(), getGoogleStatus(), getTowns(), getApifyStatus(),
      ]);
      setStats(s.data);
      setJobs(j.data);
      setGoogleStatus(gs.data);
      setTowns(t.data);
      setApifyStatus(as.data);
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'admin')) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && user.role === 'admin') {
      loadMeta();
      getCouncils()
        .then((res) => setCouncils(res.data.councils || []))
        .catch(() => toast.error('Could not load councils from FHRS. Check connection.'));
    }
  }, [user, loadMeta]);

  const pollJob = useCallback((jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await getScrapeStatus(jobId);
        setActiveJob(res.data);
        if (res.data.status === 'completed' || res.data.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          loadMeta();
          if (res.data.status === 'completed') {
            toast.success(`Import complete: ${res.data.imported} added, ${res.data.updated} updated`);
          } else {
            toast.error(`Import failed: ${res.data.error || 'unknown error'}`);
          }
        }
      } catch (e) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, 2000);
  }, [loadMeta]);

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (bfPollRef.current) clearInterval(bfPollRef.current);
    if (emailPollRef.current) clearInterval(emailPollRef.current);
    if (socialPollRef.current) clearInterval(socialPollRef.current);
  }, []);

  const pollGeneric = useCallback((jobId, ref, setJob, doneMsg) => {
    if (ref.current) clearInterval(ref.current);
    ref.current = setInterval(async () => {
      try {
        const res = await getScrapeStatus(jobId);
        setJob(res.data);
        if (res.data.status === 'completed' || res.data.status === 'failed') {
          clearInterval(ref.current);
          ref.current = null;
          loadMeta();
          if (res.data.status === 'completed') {
            toast.success(doneMsg(res.data));
          } else {
            toast.error(`Failed: ${res.data.error || 'unknown error'}`);
          }
        }
      } catch (e) {
        clearInterval(ref.current);
        ref.current = null;
      }
    }, 2500);
  }, [loadMeta]);

  const handleEmailEnrich = async () => {
    setEmailSubmitting(true);
    setEmailJob(null);
    try {
      const res = await startEmailEnrich({
        town_id: emailTown === 'all' ? null : emailTown,
        limit: Number(emailLimit) || 50,
      });
      setEmailJob({ status: 'pending', processed: 0, imported: 0, total: 0 });
      pollGeneric(res.data.job_id, emailPollRef, setEmailJob, (d) => `Email enrichment complete: ${d.imported} emails found`);
      toast.success('Email enrichment started');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to start email enrichment';
      toast.error(typeof msg === 'string' ? msg : 'Failed to start email enrichment');
    } finally {
      setEmailSubmitting(false);
    }
  };

  const handleSocialEnrich = async () => {
    setSocialSubmitting(true);
    setSocialJob(null);
    try {
      const res = await startSocialEnrich({
        town_id: socialTown === 'all' ? null : socialTown,
        limit: Number(socialLimit) || 25,
      });
      setSocialJob({ status: 'pending', processed: 0, imported: 0, total: 0 });
      pollGeneric(res.data.job_id, socialPollRef, setSocialJob, (d) => `Social lookup complete: ${d.imported} pages found`);
      toast.success('Social lookup started');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to start social lookup';
      toast.error(typeof msg === 'string' ? msg : 'Failed to start social lookup');
    } finally {
      setSocialSubmitting(false);
    }
  };

  const pollBackfill = useCallback((jobId) => {
    if (bfPollRef.current) clearInterval(bfPollRef.current);
    bfPollRef.current = setInterval(async () => {
      try {
        const res = await getScrapeStatus(jobId);
        setBfJob(res.data);
        if (res.data.status === 'completed' || res.data.status === 'failed') {
          clearInterval(bfPollRef.current);
          bfPollRef.current = null;
          loadMeta();
          if (res.data.status === 'completed') {
            toast.success(`Backfill complete: ${res.data.imported} restaurants enriched`);
          } else {
            toast.error(`Backfill failed: ${res.data.error || 'unknown error'}`);
          }
        }
      } catch (e) {
        clearInterval(bfPollRef.current);
        bfPollRef.current = null;
      }
    }, 2000);
  }, [loadMeta]);

  const handleBackfill = async () => {
    setBfSubmitting(true);
    setBfJob(null);
    try {
      const res = await startGoogleBackfill({
        town_id: bfTown === 'all' ? null : bfTown,
        limit: Number(bfLimit) || 20,
      });
      setBfJob({ status: 'pending', processed: 0, imported: 0, total: 0 });
      pollBackfill(res.data.job_id);
      toast.success('Backfill started');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to start backfill';
      toast.error(typeof msg === 'string' ? msg : 'Failed to start backfill');
    } finally {
      setBfSubmitting(false);
    }
  };

  const handlePurge = async () => {
    const count = stats?.legacy || 0;
    if (!window.confirm(`Permanently delete ${count} legacy FHRS venue(s) (canteens, care homes, etc.)? Your OpenStreetMap venues and demo restaurants are kept. This cannot be undone.`)) {
      return;
    }
    setPurging(true);
    try {
      const res = await purgeLegacy();
      toast.success(`Removed ${res.data.deleted} legacy venues and ${res.data.removed_towns} empty towns`);
      loadMeta();
    } catch (error) {
      toast.error('Failed to purge legacy data');
    } finally {
      setPurging(false);
    }
  };

  const toggleType = (val) => {    setTypes((prev) => (prev.includes(val) ? prev.filter((t) => t !== val) : [...prev, val]));
  };

  const handleStart = async () => {
    if (!authority.trim()) {
      toast.error('Please enter a council name');
      return;
    }
    if (types.length === 0) {
      toast.error('Select at least one business type');
      return;
    }
    setSubmitting(true);
    setActiveJob(null);
    try {
      const res = await startScrape({
        authority: authority.trim(),
        town: town.trim() || null,
        types,
        attach_hygiene: attachHygiene,
        include_google: includeGoogle,
      });
      setActiveJob({ status: 'pending', imported: 0, updated: 0, processed: 0, total: 0 });
      pollJob(res.data.job_id);
      toast.success('Import started');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to start import';
      toast.error(typeof msg === 'string' ? msg : 'Failed to start import');
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || !user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#7B2CBF]" />
      </div>
    );
  }

  const running = activeJob && (activeJob.status === 'pending' || activeJob.status === 'running');
  const pct = activeJob && activeJob.total > 0
    ? Math.round((activeJob.processed / activeJob.total) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="admin-page">
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[#242E30]">Admin Panel</h1>
            <p className="text-[#595959] text-sm">Import real dining venues from OpenStreetMap; hygiene ratings matched from FHRS.</p>
          </div>
          <Button onClick={() => navigate('/admin/restaurants')} className="bg-[#242E30] hover:bg-black text-white" data-testid="view-directory-btn">
            <Store className="h-4 w-4 mr-2" /> Restaurant Directory
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <StatCard icon={Store} label="Restaurants" value={stats?.restaurants} />
          <StatCard icon={Database} label="Imported" value={stats?.scraped} />
          <StatCard icon={MapPin} label="Towns" value={stats?.towns} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Import form */}
          <div className="lg:col-span-3 bg-white border border-[#E8E8E8] rounded-xl p-6">
            <div className="flex items-center gap-2 mb-5">
              <Building2 className="h-5 w-5 text-[#7B2CBF]" />
              <h2 className="text-lg font-bold text-[#242E30]">Scrape by Council</h2>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-sm">Council / Local Authority *</Label>
                <Input
                  list="councils-list"
                  value={authority}
                  onChange={(e) => setAuthority(e.target.value)}
                  placeholder="e.g., Sefton"
                  disabled={running}
                  data-testid="council-input"
                />
                <datalist id="councils-list">
                  {councils.map((c) => <option key={c} value={c} />)}
                </datalist>
                <p className="text-xs text-[#595959] mt-1">
                  {councils.length > 0 ? `${councils.length} UK councils available` : 'Loading councils...'}
                </p>
              </div>

              <div>
                <Label className="text-sm">Town filter (optional)</Label>
                <Input
                  value={town}
                  onChange={(e) => setTown(e.target.value)}
                  placeholder="e.g., Southport"
                  disabled={running}
                  data-testid="town-filter-input"
                />
                <p className="text-xs text-[#595959] mt-1">Only keep records whose address mentions this town. Recommended for large councils.</p>
              </div>

              <div>
                <Label className="text-sm mb-2 block">Business types *</Label>
                <div className="grid grid-cols-2 gap-2">
                  {BUSINESS_TYPES.map((bt) => (
                    <label
                      key={bt.value}
                      className={`flex items-center gap-2 p-2.5 border rounded-lg cursor-pointer text-sm font-medium transition-colors ${types.includes(bt.value) ? 'bg-[#7B2CBF] text-white border-[#7B2CBF]' : 'border-[#E8E8E8] hover:border-[#7B2CBF]'}`}
                      data-testid={`type-${bt.value}`}
                    >
                      <Checkbox
                        checked={types.includes(bt.value)}
                        onCheckedChange={() => toggleType(bt.value)}
                        disabled={running}
                        className="sr-only"
                      />
                      {bt.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-2 pt-1">
                <label className="flex items-center gap-2 text-sm text-[#242E30] cursor-pointer">
                  <Checkbox checked={attachHygiene} onCheckedChange={(v) => setAttachHygiene(!!v)} disabled={running} data-testid="attach-hygiene" />
                  Attach Food Hygiene ratings from FHRS (matched by location)
                </label>
                {googleStatus?.configured && (
                  <label className="flex items-center gap-2 text-sm text-[#242E30] cursor-pointer">
                    <Checkbox checked={includeGoogle} onCheckedChange={(v) => setIncludeGoogle(!!v)} disabled={running} data-testid="include-google" />
                    Fallback to Google Places for phone/website (up to 25, paid)
                  </label>
                )}
              </div>

              <Button
                onClick={handleStart}
                disabled={running || submitting}
                className="w-full bg-[#7B2CBF] hover:bg-[#5A189A] text-white"
                data-testid="start-scrape-btn"
              >
                {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <DownloadCloud className="h-4 w-4 mr-2" />}
                {running ? 'Importing...' : 'Start Import'}
              </Button>
            </div>

            {/* Progress */}
            {activeJob && (
              <div className="mt-6 p-4 rounded-lg bg-[#F5F5F5] border border-[#E8E8E8]" data-testid="scrape-progress">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-[#242E30] capitalize flex items-center gap-2">
                    {activeJob.status === 'completed' && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                    {activeJob.status === 'failed' && <XCircle className="h-4 w-4 text-red-500" />}
                    {running && <Loader2 className="h-4 w-4 text-[#7B2CBF] animate-spin" />}
                    {activeJob.status}
                  </span>
                  <span className="text-xs text-[#595959]">
                    {activeJob.processed || 0} / {activeJob.total || 0}
                  </span>
                </div>
                <div className="w-full h-2 bg-[#E8E8E8] rounded-full overflow-hidden">
                  <div className="h-full bg-[#7B2CBF] transition-all" style={{ width: `${activeJob.status === 'completed' ? 100 : pct}%` }} />
                </div>
                <div className="flex gap-4 mt-3 text-xs text-[#242E30]">
                  <span><b>{activeJob.imported || 0}</b> added</span>
                  <span><b>{activeJob.updated || 0}</b> updated</span>
                  {activeJob.hygiene_matched > 0 && <span><b>{activeJob.hygiene_matched}</b> hygiene-matched</span>}
                  {activeJob.google_enriched > 0 && <span><b>{activeJob.google_enriched}</b> Google-enriched</span>}
                </div>
                {activeJob.error && <p className="text-xs text-red-500 mt-2">{activeJob.error}</p>}
                {activeJob.osm_error && <p className="text-xs text-amber-600 mt-2">OSM enrichment skipped: {activeJob.osm_error}</p>}
              </div>
            )}
          </div>

          {/* Recent jobs */}
          <div className="lg:col-span-2 bg-white border border-[#E8E8E8] rounded-xl p-6">
            <h2 className="text-lg font-bold text-[#242E30] mb-4">Recent Imports</h2>
            {jobs.length === 0 ? (
              <p className="text-sm text-[#595959]">No imports yet.</p>
            ) : (
              <div className="space-y-3" data-testid="recent-jobs">
                {jobs.map((j) => (
                  <div key={j.job_id} className="flex items-start justify-between border-b border-[#F0F0F0] pb-3 last:border-0">
                    <div>
                      <p className="text-sm font-semibold text-[#242E30]">
                        {j.authority}{j.town ? ` · ${j.town}` : ''}
                      </p>
                      <p className="text-xs text-[#595959]">
                        {(j.types || []).join(', ')} · {j.imported || 0} added, {j.updated || 0} updated
                      </p>
                    </div>
                    <span className={`text-xs font-medium flex items-center gap-1 whitespace-nowrap ${j.status === 'completed' ? 'text-green-600' : j.status === 'failed' ? 'text-red-500' : 'text-[#7B2CBF]'}`}>
                      {j.status === 'completed' && <CheckCircle2 className="h-3.5 w-3.5" />}
                      {j.status === 'failed' && <XCircle className="h-3.5 w-3.5" />}
                      {(j.status === 'running' || j.status === 'pending') && <Clock className="h-3.5 w-3.5" />}
                      {j.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[11px] text-[#8A8A8A] mt-6 leading-relaxed">
              Data sources: Contains FSA data (Open Government Licence) and © OpenStreetMap contributors.
            </p>
          </div>
        </div>

        {/* Google contact backfill */}
        <div className="mt-6 bg-white border border-[#E8E8E8] rounded-xl p-6" data-testid="google-backfill-card">
          <div className="flex items-center gap-2 mb-1">
            <Phone className="h-5 w-5 text-[#7B2CBF]" />
            <h2 className="text-lg font-bold text-[#242E30]">Backfill Contact Info (Google)</h2>
          </div>
          <p className="text-sm text-[#595959] mb-5">
            Fill in missing phone &amp; website using Google Places.
            {googleStatus && (
              <span> {googleStatus.missing_contact} restaurants currently missing contact info.</span>
            )}
          </p>

          {!googleStatus?.configured ? (
            <p className="text-sm text-amber-600">Google Places API key not configured on the server.</p>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-end gap-4">
              <div className="flex-1">
                <Label className="text-sm">Town</Label>
                <Select value={bfTown} onValueChange={setBfTown}>
                  <SelectTrigger data-testid="bf-town-select"><SelectValue placeholder="All towns" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All towns</SelectItem>
                    {towns.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}{t.region ? `, ${t.region}` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-full sm:w-40">
                <Label className="text-sm">Batch size (max 200)</Label>
                <Input
                  type="number" min={1} max={200} value={bfLimit}
                  onChange={(e) => setBfLimit(e.target.value)}
                  disabled={bfJob && (bfJob.status === 'pending' || bfJob.status === 'running')}
                  data-testid="bf-limit-input"
                />
              </div>
              <Button
                onClick={handleBackfill}
                disabled={bfSubmitting || (bfJob && (bfJob.status === 'pending' || bfJob.status === 'running'))}
                className="bg-[#7B2CBF] hover:bg-[#5A189A] text-white"
                data-testid="start-backfill-btn"
              >
                {bfJob && (bfJob.status === 'pending' || bfJob.status === 'running')
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <Phone className="h-4 w-4 mr-2" />}
                {bfJob && (bfJob.status === 'pending' || bfJob.status === 'running') ? 'Enriching...' : 'Backfill Contact Info'}
              </Button>
            </div>
          )}

          {bfJob && (
            <div className="mt-5 p-4 rounded-lg bg-[#F5F5F5] border border-[#E8E8E8]" data-testid="backfill-progress">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-[#242E30] capitalize flex items-center gap-2">
                  {bfJob.status === 'completed' && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                  {bfJob.status === 'failed' && <XCircle className="h-4 w-4 text-red-500" />}
                  {(bfJob.status === 'pending' || bfJob.status === 'running') && <Loader2 className="h-4 w-4 text-[#7B2CBF] animate-spin" />}
                  {bfJob.status}
                </span>
                <span className="text-xs text-[#595959]">{bfJob.processed || 0} / {bfJob.total || 0}</span>
              </div>
              <div className="w-full h-2 bg-[#E8E8E8] rounded-full overflow-hidden">
                <div className="h-full bg-[#7B2CBF] transition-all" style={{ width: `${bfJob.status === 'completed' ? 100 : (bfJob.total > 0 ? Math.round((bfJob.processed / bfJob.total) * 100) : 0)}%` }} />
              </div>
              <p className="text-xs text-[#242E30] mt-3"><b>{bfJob.imported || 0}</b> restaurants enriched with phone/website</p>
              {bfJob.error && <p className="text-xs text-red-500 mt-2">{bfJob.error}</p>}
            </div>
          )}
        </div>

        {/* Apify: Email enrichment (Track A - venues WITH a website) */}
        <div className="mt-6 bg-white border border-[#E8E8E8] rounded-xl p-6" data-testid="email-enrich-card">
          <div className="flex items-center gap-2 mb-1">
            <Mail className="h-5 w-5 text-[#7B2CBF]" />
            <h2 className="text-lg font-bold text-[#242E30]">Find Emails (venues with a website)</h2>
          </div>
          <p className="text-sm text-[#595959] mb-5">
            Crawls each venue's own website for a contact email (via Apify — never touches Google).
            {apifyStatus && (
              <span> {apifyStatus.email_candidates} not-yet-checked venue(s) with a website will be searched · {apifyStatus.with_email} already have an email. Already-checked venues are skipped for {apifyStatus.check_window_days || 30} days.</span>
            )}
          </p>
          {!apifyStatus?.configured ? (
            <p className="text-sm text-amber-600">Apify token not configured on the server.</p>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-end gap-4">
              <div className="flex-1">
                <Label className="text-sm">Town</Label>
                <Select value={emailTown} onValueChange={setEmailTown}>
                  <SelectTrigger data-testid="email-town-select"><SelectValue placeholder="All towns" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All towns</SelectItem>
                    {towns.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}{t.region ? `, ${t.region}` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-full sm:w-40">
                <Label className="text-sm">Batch size (max 500)</Label>
                <Input
                  type="number" min={1} max={500} value={emailLimit}
                  onChange={(e) => setEmailLimit(e.target.value)}
                  disabled={emailJob && (emailJob.status === 'pending' || emailJob.status === 'running')}
                  data-testid="email-limit-input"
                />
              </div>
              <Button
                onClick={handleEmailEnrich}
                disabled={emailSubmitting || (emailJob && (emailJob.status === 'pending' || emailJob.status === 'running'))}
                className="bg-[#7B2CBF] hover:bg-[#5A189A] text-white"
                data-testid="start-email-enrich-btn"
              >
                {emailJob && (emailJob.status === 'pending' || emailJob.status === 'running')
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <Mail className="h-4 w-4 mr-2" />}
                {emailJob && (emailJob.status === 'pending' || emailJob.status === 'running') ? 'Finding…' : 'Find Emails'}
              </Button>
            </div>
          )}
          {emailJob && (
            <div className="mt-5 p-4 rounded-lg bg-[#F5F5F5] border border-[#E8E8E8]" data-testid="email-enrich-progress">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-[#242E30] capitalize flex items-center gap-2">
                  {emailJob.status === 'completed' && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                  {emailJob.status === 'failed' && <XCircle className="h-4 w-4 text-red-500" />}
                  {(emailJob.status === 'pending' || emailJob.status === 'running') && <Loader2 className="h-4 w-4 text-[#7B2CBF] animate-spin" />}
                  {emailJob.status}
                </span>
                <span className="text-xs text-[#595959]">{emailJob.processed || 0} / {emailJob.total || 0}</span>
              </div>
              <div className="w-full h-2 bg-[#E8E8E8] rounded-full overflow-hidden">
                <div className="h-full bg-[#7B2CBF] transition-all" style={{ width: `${emailJob.status === 'completed' ? 100 : (emailJob.total > 0 ? Math.round((emailJob.processed / emailJob.total) * 100) : 0)}%` }} />
              </div>
              <p className="text-xs text-[#242E30] mt-3"><b>{emailJob.imported || 0}</b> emails found</p>
              {emailJob.error && <p className="text-xs text-red-500 mt-2">{emailJob.error}</p>}
            </div>
          )}
        </div>

        {/* Apify: Social lookup (Track B - venues with NO website = website-service prospects) */}
        <div className="mt-6 bg-white border border-[#E8E8E8] rounded-xl p-6" data-testid="social-enrich-card">
          <div className="flex items-center gap-2 mb-1">
            <Share2 className="h-5 w-5 text-[#7B2CBF]" />
            <h2 className="text-lg font-bold text-[#242E30]">Find Socials (venues with no website)</h2>
          </div>
          <p className="text-sm text-[#595959] mb-5">
            Your website-service prospects. Searches Facebook by name + town to find a page you can contact (via Apify).
            {apifyStatus && (
              <span> {apifyStatus.social_candidates} not-yet-checked websiteless venue(s) will be searched · {apifyStatus.with_social} already have a social. Already-checked venues are skipped for {apifyStatus.check_window_days || 30} days.</span>
            )}
          </p>
          {!apifyStatus?.configured ? (
            <p className="text-sm text-amber-600">Apify token not configured on the server.</p>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-end gap-4">
              <div className="flex-1">
                <Label className="text-sm">Town</Label>
                <Select value={socialTown} onValueChange={setSocialTown}>
                  <SelectTrigger data-testid="social-town-select"><SelectValue placeholder="All towns" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All towns</SelectItem>
                    {towns.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}{t.region ? `, ${t.region}` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-full sm:w-40">
                <Label className="text-sm">Batch size (max 100)</Label>
                <Input
                  type="number" min={1} max={100} value={socialLimit}
                  onChange={(e) => setSocialLimit(e.target.value)}
                  disabled={socialJob && (socialJob.status === 'pending' || socialJob.status === 'running')}
                  data-testid="social-limit-input"
                />
              </div>
              <Button
                onClick={handleSocialEnrich}
                disabled={socialSubmitting || (socialJob && (socialJob.status === 'pending' || socialJob.status === 'running'))}
                className="bg-[#7B2CBF] hover:bg-[#5A189A] text-white"
                data-testid="start-social-enrich-btn"
              >
                {socialJob && (socialJob.status === 'pending' || socialJob.status === 'running')
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <Share2 className="h-4 w-4 mr-2" />}
                {socialJob && (socialJob.status === 'pending' || socialJob.status === 'running') ? 'Searching…' : 'Find Socials'}
              </Button>
            </div>
          )}
          {socialJob && (
            <div className="mt-5 p-4 rounded-lg bg-[#F5F5F5] border border-[#E8E8E8]" data-testid="social-enrich-progress">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-[#242E30] capitalize flex items-center gap-2">
                  {socialJob.status === 'completed' && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                  {socialJob.status === 'failed' && <XCircle className="h-4 w-4 text-red-500" />}
                  {(socialJob.status === 'pending' || socialJob.status === 'running') && <Loader2 className="h-4 w-4 text-[#7B2CBF] animate-spin" />}
                  {socialJob.status}
                </span>
                <span className="text-xs text-[#595959]">{socialJob.processed || 0} / {socialJob.total || 0}</span>
              </div>
              <div className="w-full h-2 bg-[#E8E8E8] rounded-full overflow-hidden">
                <div className="h-full bg-[#7B2CBF] transition-all" style={{ width: `${socialJob.status === 'completed' ? 100 : (socialJob.total > 0 ? Math.round((socialJob.processed / socialJob.total) * 100) : 0)}%` }} />
              </div>
              <p className="text-xs text-[#242E30] mt-3"><b>{socialJob.imported || 0}</b> Facebook pages found</p>
              {socialJob.error && <p className="text-xs text-red-500 mt-2">{socialJob.error}</p>}
            </div>
          )}
        </div>

        {/* Purge legacy FHRS data */}
        {stats?.legacy > 0 && (
          <div className="mt-6 bg-white border border-[#F3C0BC] rounded-xl p-6" data-testid="purge-legacy-card">
            <div className="flex items-center gap-2 mb-1">
              <Trash2 className="h-5 w-5 text-[#D4351C]" />
              <h2 className="text-lg font-bold text-[#242E30]">Remove Legacy FHRS Data</h2>
            </div>
            <p className="text-sm text-[#595959] mb-4">
              You have <b>{stats.legacy}</b> venue(s) imported from the old FHRS source, which can include
              staff canteens, care homes and school/hospital catering. Removing them leaves only your clean
              OpenStreetMap venues (and demo restaurants). This cannot be undone.
            </p>
            <Button
              onClick={handlePurge}
              disabled={purging}
              className="bg-[#D4351C] hover:bg-[#aa2a16] text-white"
              data-testid="purge-legacy-btn"
            >
              {purging ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              {purging ? 'Removing...' : `Remove ${stats.legacy} legacy venues`}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
